import { Injectable } from '@angular/core';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto'; 
import type { RatePlanFindInterface } from '@zaoblako/shared/modules/saas/dtos/rate-plan/rate-plan-find.dto';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import { BasisRatePlanService } from '@zaoblako/web/core/services/basis/basis-rate-plan.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TitleService } from '@zaoblako/web/core/services/title.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { TenantState } from '@zaoblako/web/modules/admin/components/tenant/card/models/tenant-state';
import { tenantInitialState } from '@zaoblako/web/modules/admin/components/tenant/card/state/tenant-initial-state';
import { BehaviorSubject, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TenantStoreService extends StoreService<TenantState> {
  protected state = new BehaviorSubject(tenantInitialState);

  public get getState(): TenantState {
    return this.state.value;
  }

  public constructor(
    private readonly tenantService: TenantService,
    private readonly basisRatePlanService: BasisRatePlanService,
    private readonly globalService: GlobalService,
    private readonly titleService: TitleService
  ) {
    super();
  }

  public loadState(tenantId: number) {
    this.loadTenant(tenantId);
    this.loadEntities();
    this.loadPanelsInfo();
  }

  public updateFieldTenant(newTenant: TenantFindInterface): Observable<TenantFindInterface> {
    const oldTenant: TenantFindInterface = { ...this.getState.tenant };
    return this.tenantService.update<TenantFindInterface, TenantFindInterface>(newTenant).pipe(
      tap({
        next: () => {
          if (this.globalService.globalInfo.tenantInfo?.id === newTenant.id) {
            this.globalService.updateTenant(newTenant);
          }
          this.updateState({ tenant: { ...newTenant } });
        },
        error: () => {
          this.updateState({ tenant: oldTenant });
        }
      })
    );
  }

  public updateContragent(contragent: ContragentFindInterface) {
    const tenant: TenantFindInterface = { ...this.getState.tenant };
    tenant.representativeContragent = contragent;
    this.updateState({
      tenant
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.state.value.panelsInfo, any>, value: any) {
    const panelsInfo = { ...this.state.value.panelsInfo };
    panelsInfo[key] = value;
    this.updateState({ panelsInfo });
    localStorage.setItem('tenant.panelsInfo', JSON.stringify(panelsInfo));
  }

  public loadTransaction(queryParams: PaginationRequiredQueryInterface) {
    this.updateState((state) => ({
      transactionsInfo: {
        ...state.transactionsInfo,
        queryParams,
        loading: true
      }
    }));

    this.tenantService
      .getTransactionHistory(this.getState.tenant?.id, this.getState.transactionsInfo?.queryParams)
      .subscribe({
        next: (data) => {
          this.updateState((state) => ({
            transactionsInfo: {
              ...state.transactionsInfo,
              count: data.count,
              items: data.transactions,
              loading: false
            }
          }));
        }
      });
  }

  public onScrollEndTransaction() {
    let transactionsInfo = this.getState.transactionsInfo;
    if (
      transactionsInfo?.items?.length < transactionsInfo?.count &&
      !transactionsInfo?.loading &&
      !transactionsInfo?.paginationLoading
    ) {
      this.updateState((state) => ({
        transactionsInfo: {
          ...state.transactionsInfo,
          queryParams: {
            ...transactionsInfo.queryParams,
            offset: transactionsInfo.queryParams.offset + transactionsInfo.queryParams.limit
          },
          paginationLoading: true
        }
      }));

      this.tenantService
        .getTransactionHistory(this.getState.tenant?.id, this.getState.transactionsInfo?.queryParams)
        .subscribe({
          next: (data) => {
            transactionsInfo = this.getState.transactionsInfo;
            this.updateState((state) => ({
              transactionsInfo: {
                ...state.transactionsInfo,
                count: data?.count,
                items: transactionsInfo.items.concat(data.transactions),
                paginationLoading: false
              }
            }));
          }
        });
    }
  }

  public loadStatistics() {
    this.updateState((state) => ({
      statisticsInfo: {
        ...state.statisticsInfo,
        loading: true
      }
    }));

    this.tenantService.getStatistics(this.getState.tenant?.id).subscribe({
      next: (data) => {
        this.updateState((state) => ({
          statisticsInfo: {
            ...state.statisticsInfo,
            statistics: data,
            loading: false
          }
        }));
      }
    });
  }

  public loadHistory(queryParams: PaginationQueryInterface) {
    this.updateState((state) => ({
      historyInfo: {
        ...state.historyInfo,
        queryParams,
        loading: true
      }
    }));

    this.tenantService.getHistory(this.getState.tenant?.id, this.getState.historyInfo?.queryParams).subscribe({
      next: (data) => {
        this.updateState((state) => ({
          historyInfo: {
            ...state.historyInfo,
            count: data.count,
            items: data.items,
            loading: false
          }
        }));
      }
    });
  }

  public onScrollEndHistory() {
    let historyInfo = this.getState.historyInfo;
    if (historyInfo?.items?.length < historyInfo?.count && !historyInfo?.loading && !historyInfo?.paginationLoading) {
      this.updateState((state) => ({
        historyInfo: {
          ...state.historyInfo,
          queryParams: {
            ...historyInfo.queryParams,
            offset: historyInfo.queryParams.offset + historyInfo.queryParams.limit
          },
          paginationLoading: true
        }
      }));

      this.tenantService.getHistory(this.getState.tenant?.id, this.getState.historyInfo?.queryParams).subscribe({
        next: (data) => {
          historyInfo = this.getState.historyInfo;
          this.updateState((state) => ({
            historyInfo: {
              ...state.historyInfo,
              count: data?.count,
              items: historyInfo.items.concat(data.items),
              paginationLoading: false
            }
          }));
        }
      });
    }
  }

  private loadTenant(tenantId: number) {
    this.updateState({ loading: true });

    this.tenantService.findById<TenantFindInterface>(tenantId).subscribe({
      next: (tenant) => {
        this.titleService.setTitle(`Тенант "${tenant?.name}"`);
        this.updateState({
          loading: false,
          tenant
        });
      }
    });
  }

  private loadPanelsInfo() {
    if (localStorage.getItem('tenant.panelsInfo')) {
      this.updateState({ panelsInfo: JSON.parse(localStorage.getItem('tenant.panelsInfo')) });
    }
  }

  private loadEntities() {
    this.basisRatePlanService.find<RatePlanFindInterface>().subscribe({
      next: (ratePlans) => {
        this.updateState({
          ratePlans
        });
      }
    });
  }
}
